import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Separator } from '@/components/ui/separator';
import { Plus, X, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface FilterOption {
  value: string;
  label: string;
  /** Color class for a dot indicator (e.g. "bg-green-500") */
  color?: string;
  /** Icon rendered next to the option label */
  icon?: React.ComponentType<{ className?: string }>;
  /** Count shown next to the option */
  count?: number;
}

export type FilterFieldType = 'multi-select' | 'text' | 'number' | 'date' | 'date-range';

export interface FilterDefinition {
  key: string;
  label: string;
  type: FilterFieldType;
  /** Icon rendered on the chip and in the "add filter" menu */
  icon?: React.ComponentType<{ className?: string }>;
  /** Options for "multi-select" type */
  options?: FilterOption[];
  /** Operators for text/number/date. Defaults provided per type if omitted. */
  operators?: { value: string; label: string }[];
  /** Placeholder text for input-based filters */
  placeholder?: string;
  /**
   * Optional custom popover content. When set, FilterBar renders this
   * instead of the default editor — useful when a filter needs richer
   * UI than the built-in checkbox list (e.g. an AssigneePicker with
   * avatars, group headings, role names).
   *
   * The callback receives the current selection (as `string[]` to match
   * the multi-select wire shape — single-select pickers can ignore
   * everything past index 0), plus toggle/clear/close handlers wired
   * into the same FilterBar state machine the default editor uses.
   *
   * **Single-select consumers** must implement the swap pattern
   * manually: clear the previous value (if any) before setting the
   * new one. `onToggle` is shape-faithful to multi-select, so setting
   * a new value without first removing the old one yields a
   * two-element selection — and most URL serializers only honor the
   * first entry, so the second silently disappears on round-trip.
   *
   * ```tsx
   * customEditor: ({ selected, onToggle, onClose }) => {
   *   const prev = selected[0];
   *   return <Picker onPick={(next) => {
   *     if (prev) onToggle(prev, false); // clear previous
   *     onToggle(next, true);             // set new
   *     onClose();
   *   }} />;
   * }
   * ```
   */
  customEditor?: (props: {
    selected: string[];
    onToggle: (value: string, checked: boolean) => void;
    onClear: () => void;
    onClose: () => void;
  }) => React.ReactNode;
  /**
   * Optional custom width for the popover content (Tailwind class).
   * Defaults to `w-56` (matches the default editor). Custom editors
   * with richer rows usually want more room — e.g. `w-[280px]`.
   */
  popoverWidthClass?: string;
}

export type FilterValue =
  | { type: 'multi-select'; values: string[] }
  | { type: 'input'; operator: string; value: string }
  | { type: 'date-range'; from: string; to: string };

/** Filter state: mapping from filter key to its current value */
export type FilterState = Record<string, FilterValue>;

export interface FilterBarProps {
  /** Available filter definitions */
  definitions: FilterDefinition[];
  /** Current filter state */
  value: FilterState;
  /** Called when filters change */
  onChange: (state: FilterState) => void;
  /** Optional className for the wrapper */
  className?: string;
  /**
   * Content rendered before the filter chips (e.g. a search input).
   * A vertical separator is automatically inserted between children and chips.
   */
  children?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Default operators per type
// ---------------------------------------------------------------------------

const DEFAULT_NUMBER_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'gte', label: 'at least' },
  { value: 'lte', label: 'at most' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
];

const DEFAULT_DATE_OPERATORS = [
  { value: 'eq', label: 'on' },
  { value: 'gte', label: 'after' },
  { value: 'lte', label: 'before' },
];

const DEFAULT_TEXT_OPERATORS = [
  { value: 'contains', label: 'contains' },
  { value: 'eq', label: 'equals' },
  { value: 'starts_with', label: 'starts with' },
];

/**
 * True iff `v` carries a non-empty payload — a multi-select with at
 * least one chosen option, a date-range with either bound, or a
 * scalar with a non-blank value. Centralized here so every consumer
 * (toolbar dot indicators, chip rendering, future clear-all wiring)
 * shares one definition; if the FilterValue union grows a 4th variant,
 * this is the one place to update.
 */
export function isActiveFilterValue(v: FilterValue): boolean {
  if (v.type === 'multi-select') return v.values.length > 0;
  if (v.type === 'date-range') return v.from !== '' || v.to !== '';
  return v.value !== '';
}

/**
 * True iff any key in `state` has an active value. O(keys) scan;
 * cheaper than a useMemo for the typical handful of filter keys.
 */
export function hasAnyActiveValue(state: FilterState): boolean {
  for (const v of Object.values(state)) {
    if (isActiveFilterValue(v)) return true;
  }
  return false;
}

function getDefaultOperators(type: FilterFieldType): { value: string; label: string }[] {
  switch (type) {
    case 'number':
      return DEFAULT_NUMBER_OPERATORS;
    case 'date':
      return DEFAULT_DATE_OPERATORS;
    case 'text':
      return DEFAULT_TEXT_OPERATORS;
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSelectedLabels(definition: FilterDefinition, selected: string[]): string {
  if (selected.length === 0) return '';
  const firstVal = selected[0] ?? '';
  if (selected.length === 1) {
    return definition.options?.find((o) => o.value === firstVal)?.label ?? firstVal;
  }
  if (selected.length <= 2) {
    return selected
      .map((v) => definition.options?.find((o) => o.value === v)?.label ?? v)
      .join(', ');
  }
  const firstLabel = definition.options?.find((o) => o.value === firstVal)?.label ?? firstVal;
  return `${firstLabel} +${selected.length - 1}`;
}

function getOperatorLabel(definition: FilterDefinition, operator: string): string {
  const operators = definition.operators ?? getDefaultOperators(definition.type);
  return operators.find((o) => o.value === operator)?.label ?? operator;
}

/** Format a date-range filter for display on the chip. */
export function formatDateRangeDisplay(from: string, to: string): string {
  const fmt = (d: string): string => {
    const [year, month, day] = d.split('-');
    if (!year || !month || !day) return d;
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const monthName = months[parseInt(month, 10) - 1] ?? month;
    return `${monthName} ${parseInt(day, 10)}`;
  };

  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `after ${fmt(from)}`;
  if (to) return `before ${fmt(to)}`;
  return '';
}

// ---------------------------------------------------------------------------
// MultiSelectEditor – checkbox list for multi-select filters
// ---------------------------------------------------------------------------

function MultiSelectEditor({
  definition,
  selected,
  onToggle,
  onClear,
  onSelectAll,
}: {
  definition: FilterDefinition;
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
  onClear: () => void;
  onSelectAll: () => void;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const options = definition.options ?? [];

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <div className="flex flex-col">
      {options.length > 5 && (
        <div className="px-2 pt-2 pb-1">
          <Input
            placeholder={`Search ${definition.label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
      )}

      <ScrollArea className={cn(filtered.length > 8 ? 'h-52' : '')}>
        <div className="p-1">
          {filtered.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50"
            >
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={(c) => onToggle(opt.value, c as boolean)}
              />
              {opt.icon != null ? (
                <opt.icon className="size-3.5 shrink-0 text-muted-foreground" />
              ) : opt.color != null ? (
                <span className={cn('inline-block size-2 shrink-0 rounded-full', opt.color)} />
              ) : null}
              <span className="truncate text-xs">{opt.label}</span>
              {opt.count !== undefined && (
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                  {opt.count}
                </span>
              )}
              {selected.includes(opt.value) && opt.count === undefined && (
                <Check className="ml-auto size-3 text-primary" />
              )}
            </label>
          ))}
          {filtered.length === 0 && (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">No options found</p>
          )}
        </div>
      </ScrollArea>

      <Separator />
      <div className="flex items-center justify-between p-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground"
          onClick={onSelectAll}
        >
          Select all
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground"
          onClick={onClear}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InputEditor – operator + value input for text/number/date filters
// ---------------------------------------------------------------------------

function InputEditor({
  definition,
  operator,
  value,
  onApply,
}: {
  definition: FilterDefinition;
  operator: string;
  value: string;
  onApply: (operator: string, value: string) => void;
}): React.ReactElement {
  const operators = definition.operators ?? getDefaultOperators(definition.type);
  const [localOp, setLocalOp] = useState(operator || operators[0]?.value || 'eq');
  const [localValue, setLocalValue] = useState(value);

  const inputType = definition.type === 'date' ? 'date' : 'text';

  const handleApply = (): void => {
    if (localValue === '') return;
    onApply(localOp, localValue);
  };

  return (
    <div className="flex flex-col gap-2 p-3">
      {operators.length > 1 && (
        <Select value={localOp} onValueChange={setLocalOp}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {operators.map((op) => (
              <SelectItem key={op.value} value={op.value} className="text-xs">
                {op.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Input
        type={inputType}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={definition.placeholder ?? `Enter ${definition.label.toLowerCase()}...`}
        className="h-8 text-xs"
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleApply();
        }}
      />
      <Button size="sm" className="h-7 w-full text-xs" onClick={handleApply}>
        Apply
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DateRangeEditor – from/to date inputs for date-range filters
// ---------------------------------------------------------------------------

function DateRangeEditor({
  from,
  to,
  onApply,
}: {
  from: string;
  to: string;
  onApply: (from: string, to: string) => void;
}): React.ReactElement {
  const [localFrom, setLocalFrom] = useState(from);
  const [localTo, setLocalTo] = useState(to);

  return (
    <div className="flex flex-col gap-2 p-3">
      <Label className="text-xs">From</Label>
      <Input
        type="date"
        value={localFrom}
        onChange={(e) => setLocalFrom(e.target.value)}
        className="h-8 text-xs"
      />
      <Label className="text-xs">To</Label>
      <Input
        type="date"
        value={localTo}
        onChange={(e) => setLocalTo(e.target.value)}
        className="h-8 text-xs"
      />
      <Button size="sm" className="h-7 w-full text-xs" onClick={() => onApply(localFrom, localTo)}>
        Apply
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActiveFilterChip – clickable chip for an active filter
// ---------------------------------------------------------------------------

function ActiveFilterChip({
  definition,
  filterValue,
  onUpdate,
  onRemove,
  defaultOpen = false,
}: {
  definition: FilterDefinition;
  filterValue: FilterValue;
  onUpdate: (val: FilterValue) => void;
  onRemove: () => void;
  defaultOpen?: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);

  // Auto-open the value popover when this chip is freshly added via "Add filter".
  useEffect(() => {
    if (defaultOpen) {
      const t = setTimeout(() => setOpen(true), 60);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount
  const Icon = definition.icon;

  let displayText = '';
  let connectorText = 'is';

  if (filterValue.type === 'multi-select') {
    displayText = getSelectedLabels(definition, filterValue.values);
  } else if (filterValue.type === 'date-range') {
    connectorText = '';
    displayText = formatDateRangeDisplay(filterValue.from, filterValue.to);
  } else {
    connectorText = getOperatorLabel(definition, filterValue.operator);
    displayText = filterValue.value;
  }

  const handleMultiToggle = (optValue: string, checked: boolean): void => {
    if (filterValue.type !== 'multi-select') return;
    const current = filterValue.values;
    const next = checked ? [...current, optValue] : current.filter((v) => v !== optValue);
    onUpdate({ type: 'multi-select', values: next });
  };

  const handleMultiClear = (): void => {
    onUpdate({ type: 'multi-select', values: [] });
  };

  const handleMultiSelectAll = (): void => {
    const all = definition.options?.map((o) => o.value) ?? [];
    onUpdate({ type: 'multi-select', values: all });
  };

  const handleInputApply = (operator: string, value: string): void => {
    onUpdate({ type: 'input', operator, value });
    setOpen(false);
  };

  const handleDateRangeApply = (from: string, to: string): void => {
    onUpdate({ type: 'date-range', from, to });
    setOpen(false);
  };

  // Auto-remove the chip when the popover closes with no committed
  // values. Symmetric to the auto-open above: a freshly-added chip
  // that the user closes without picking anything disappears, and a
  // user who clears every value off an established chip and clicks
  // away gets the same outcome — "a filter without values is
  // meaningless." Matches Linear/Airtable behavior.
  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next && !isActiveFilterValue(filterValue)) {
      onRemove();
    }
  };

  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors',
              'hover:bg-muted/50',
              'border-border',
              open && 'ring-1 ring-ring'
            )}
          >
            {Icon != null && <Icon className="size-3 text-muted-foreground" />}
            <span className="text-muted-foreground">{definition.label}</span>
            {connectorText && <span className="text-muted-foreground">{connectorText}</span>}
            <span className="max-w-[150px] truncate font-semibold">{displayText}</span>
            <ChevronDown className="size-3 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className={cn('p-0', definition.popoverWidthClass ?? 'w-56')}>
          {definition.customEditor ? (
            // Custom editor sees the multi-select-shaped selection so
            // single-select rich pickers (e.g. AssigneePicker) can read
            // the first value and ignore the rest. Toggle/clear flow
            // through the same state machine the default editor uses.
            definition.customEditor({
              selected: filterValue.type === 'multi-select' ? filterValue.values : [],
              onToggle: handleMultiToggle,
              onClear: handleMultiClear,
              onClose: () => setOpen(false),
            })
          ) : filterValue.type === 'multi-select' ? (
            <MultiSelectEditor
              definition={definition}
              selected={filterValue.values}
              onToggle={handleMultiToggle}
              onClear={handleMultiClear}
              onSelectAll={handleMultiSelectAll}
            />
          ) : filterValue.type === 'date-range' ? (
            <DateRangeEditor
              from={filterValue.from}
              to={filterValue.to}
              onApply={handleDateRangeApply}
            />
          ) : (
            <InputEditor
              definition={definition}
              operator={filterValue.operator}
              value={filterValue.value}
              onApply={handleInputApply}
            />
          )}
        </PopoverContent>
      </Popover>
      <button
        onClick={onRemove}
        className="ml-0.5 rounded-md p-0.5 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        aria-label={`Remove ${definition.label} filter`}
      >
        <X className="size-3" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddFilterButton – the "+ Add filter" command-menu popover
// ---------------------------------------------------------------------------

function AddFilterButton({
  definitions,
  activeKeys,
  onAdd,
}: {
  definitions: FilterDefinition[];
  activeKeys: Set<string>;
  onAdd: (key: string) => void;
}): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const available = definitions.filter((d) => !activeKeys.has(d.key));

  if (available.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 border border-dashed text-muted-foreground"
        >
          <Plus className="size-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-0">
        <Command>
          <CommandInput ref={inputRef} placeholder="Search filters..." className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>No filters found.</CommandEmpty>
            <CommandGroup>
              {available.map((def) => {
                const DefIcon = def.icon;
                return (
                  <CommandItem
                    key={def.key}
                    onSelect={() => {
                      onAdd(def.key);
                      setOpen(false);
                    }}
                    className="cursor-pointer gap-2 text-xs"
                  >
                    {DefIcon != null && <DefIcon className="size-3.5 text-muted-foreground" />}
                    {def.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// FilterBar – main exported component
// ---------------------------------------------------------------------------

export function FilterBar({
  definitions,
  value,
  onChange,
  className,
  children,
}: FilterBarProps): React.ReactElement {
  // Track which filter keys are visible in the bar (added by user).
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set());
  const [autoOpenKey, setAutoOpenKey] = useState<string | null>(null);

  // Merge: keys present in value (from parent) + keys user just added
  const activeKeys = useMemo(() => {
    const keys = new Set(Object.keys(value));
    for (const k of addedKeys) keys.add(k);
    return Array.from(keys);
  }, [value, addedKeys]);

  const activeKeySet = useMemo(() => new Set(activeKeys), [activeKeys]);

  const defByKey = useMemo(() => {
    const map = new Map<string, FilterDefinition>();
    for (const d of definitions) map.set(d.key, d);
    return map;
  }, [definitions]);

  useEffect(() => {
    if (autoOpenKey != null) {
      const t = setTimeout(() => setAutoOpenKey(null), 100);
      return () => clearTimeout(t);
    }
  }, [autoOpenKey]);

  // Build a default empty FilterValue for a given definition
  const makeEmptyValue = useCallback((def: FilterDefinition): FilterValue => {
    if (def.type === 'multi-select') {
      return { type: 'multi-select', values: [] };
    }
    if (def.type === 'date-range') {
      return { type: 'date-range', from: '', to: '' };
    }
    const operators = def.operators ?? getDefaultOperators(def.type);
    return { type: 'input', operator: operators[0]?.value ?? 'eq', value: '' };
  }, []);

  const handleAdd = useCallback(
    (key: string) => {
      if (!activeKeySet.has(key)) {
        setAddedKeys((prev) => new Set(prev).add(key));
        setAutoOpenKey(key);
      }
    },
    [activeKeySet]
  );

  const handleRemove = useCallback(
    (key: string) => {
      setAddedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      const next = { ...value };
      delete next[key];
      onChange(next);
    },
    [value, onChange]
  );

  const handleUpdate = useCallback(
    (key: string, filterValue: FilterValue) => {
      onChange({ ...value, [key]: filterValue });
    },
    [value, onChange]
  );

  const hasChips = activeKeys.length > 0;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}

      {children != null && (hasChips || definitions.length > 0) && (
        <Separator orientation="vertical" className="h-4" />
      )}

      {activeKeys.map((key) => {
        const def = defByKey.get(key);
        if (def == null) return null;
        const fv = value[key] ?? makeEmptyValue(def);
        return (
          <ActiveFilterChip
            key={key}
            definition={def}
            filterValue={fv}
            onUpdate={(val) => handleUpdate(key, val)}
            onRemove={() => handleRemove(key)}
            defaultOpen={key === autoOpenKey}
          />
        );
      })}

      <AddFilterButton definitions={definitions} activeKeys={activeKeySet} onAdd={handleAdd} />

      {/*
       * Removed: clear-all (CircleX) button. It called `onChange({})`,
       * but every consumer of FilterBar (tasks, documents) seeds default
       * filter values from URL params on every render, so the cleared
       * state snapped right back and the button looked broken. The
       * per-chip × already lets users remove filters individually, and
       * the surrounding view's "back to search" toggle is the
       * affordance for leaving filter mode entirely.
       */}
    </div>
  );
}
